import _ from 'lodash';
import { ReactElement, memo, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { DynamicPdfType, Language, PdfOptions, SignatureType } from '@breathelife/types';
import { Loader } from '@breathelife/ui-components';

import { setAuthorizationInterceptor } from '../Services/ApiService';
import { getCurrency } from '../helpers/currency';
import { isTimezoneValid } from '../helpers/isTimezoneValid';
import { DynamicPdfProps } from '../types';
import { DataFetchingContainer } from './DataFetchingContainer';

const CenteredText = styled.div`
  text-align: center;
`;

function getAppropriatePdfOptionsFromPdfType(
  type: string,
  pdfProps: Omit<DynamicPdfProps, 'countryCode'>,
): PdfOptions | null {
  switch (type) {
    case DynamicPdfType.carrier:
      return pdfProps.carrierPdfOptions;
    case DynamicPdfType.insured:
      return pdfProps.insuredPdfOptions;
    case DynamicPdfType.advisor:
      return pdfProps.advisorPdfOptions;
    case DynamicPdfType.medicalInterviewAnswers:
      return pdfProps.medicalInterviewAnswersPdfOptions;
    case DynamicPdfType.pad:
      return pdfProps.padPdfOptions;
    default:
      throw Error(`Pdf type parameter ${type} not yet supported by the dynamic pdf`);
  }
}

function PdfContainerComponent(props: DynamicPdfProps): ReactElement | null {
  const {
    carrierPdfOptions,
    insuredPdfOptions,
    advisorPdfOptions,
    countryCode,
    signatureOptions,
    medicalInterviewAnswersPdfOptions,
    padPdfOptions,
  } = props;
  const [authTokenSet, setAuthTokenSet] = useState(false);
  const [signatureType, setSignatureType] = useState<SignatureType>();
  const { search } = useLocation();
  const { type = '', applicationId = '' } = useParams<{ type: string; applicationId: string }>();

  const pdfOptions = useMemo(
    () =>
      getAppropriatePdfOptionsFromPdfType(type, {
        insuredPdfOptions,
        carrierPdfOptions,
        advisorPdfOptions,
        medicalInterviewAnswersPdfOptions,
        padPdfOptions,
      }),
    [type, insuredPdfOptions, carrierPdfOptions, advisorPdfOptions, medicalInterviewAnswersPdfOptions, padPdfOptions],
  );

  const language = new URLSearchParams(search).get('lang');
  const participantId = new URLSearchParams(search).get('participantId');

  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const token = urlParams.get('token');
    const signatureTypeParams = urlParams.get('signatureType');
    const signatureTypes = Object.values(SignatureType);

    if (!signatureType && signatureTypeParams && signatureTypes.includes(signatureTypeParams as SignatureType)) {
      setSignatureType(signatureTypeParams as SignatureType);
    }

    if (!authTokenSet && token) {
      setAuthorizationInterceptor(token);
      setAuthTokenSet(true);
    }
  }, [search, authTokenSet, signatureType]);

  if (!authTokenSet) {
    return <Loader color='#000000' />;
  }

  if (!pdfOptions) {
    return <CenteredText>{`The pdf was not generated because pdf type ${type} is not enabled`}</CenteredText>;
  }

  const timezone = pdfOptions.timezone;

  if (timezone && !isTimezoneValid(timezone)) {
    throw new Error(`The pdf was not generated because the timezone ${timezone} is not valid`);
  }

  const currency = getCurrency(countryCode);

  return (
    <DataFetchingContainer
      applicationId={applicationId}
      pdfType={type as DynamicPdfType}
      pdfOptions={pdfOptions}
      currency={currency}
      signatureType={signatureType}
      language={language === Language.fr ? Language.fr : Language.en}
      eSignatureFieldType={signatureOptions?.eSignatureFieldType}
      participantId={participantId || undefined}
    />
  );
}

export const PdfContainer = memo(PdfContainerComponent, _.isEqual);
